import { fetchWithAuth } from '../../api/backendApi';
import {
  readLauncherBackgroundUrlAsync,
  writeLauncherBackgroundUrlAsync,
  readLauncherBackgroundUrlSync,
} from './launcherBackgroundStore';

export const LAUNCHER_BACKGROUND_CHANGED = 'launcher-background-changed';

export const SCHEMA_VERSION = 1;

export const EDIT_TIMEOUT_MS = 30000;

export const DEFAULT_ICONS = [];

const readStorageJson = (key) => {
  try {
    if (!key) return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Tenant-scoped layout cache. The older `launcher_layout_${uid}` key has no
 * tenant, so it is not read as the active Home. It can be used only when
 * layoutOwnedByContext proves it belongs to this tenant or workspace.
 */
export const getLayoutStorageKey = (uid, tenantId) => (
  uid && tenantId ? `launcher_layout_${uid}_${tenantId}` : null
);

export const legacyLayoutStorageKey = (uid) => (uid ? `launcher_layout_${uid}` : null);

export const readLayoutFromStorage = (uid, tenantId) => readStorageJson(getLayoutStorageKey(uid, tenantId));

export const readLegacyLayoutFromStorage = (uid) => readStorageJson(legacyLayoutStorageKey(uid));

export const layoutOwnedByContext = (cached, { tenantId, workspaceId } = {}) => {
  if (!cached || typeof cached !== 'object') return false;
  if (tenantId && cached.tenantId && cached.tenantId === tenantId) return true;
  if (workspaceId && cached.seededFromWorkspaceId === workspaceId) return true;
  return false;
};

export const getBackgroundStorageKey = (uid) => (uid ? `launcher_background_${uid}` : null);

export const readLauncherBackgroundUrl = (uid) => readLauncherBackgroundUrlSync(uid);

export const writeLauncherBackgroundUrl = (uid, url) => persistLauncherBackgroundUrl(uid, url);

export const persistLauncherBackgroundUrl = async (uid, url) => {
  await writeLauncherBackgroundUrlAsync(uid, url || '');
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(LAUNCHER_BACKGROUND_CHANGED, {
      detail: { uid, url: url || '' },
    }));
  }
};

export const resolveLauncherBackgroundUrl = (uid, fallback = '') => (
  readLauncherBackgroundUrlSync(uid) || fallback || ''
);

export const resolveLauncherBackgroundUrlAsync = async (uid, fallback = '') => {
  const stored = await readLauncherBackgroundUrlAsync(uid);
  return stored || fallback || '';
};

export const writeLayoutToStorage = (uid, layout, tenantId) => {
  try {
    const key = getLayoutStorageKey(uid, tenantId);
    if (!key) return false;
    const existing = readLayoutFromStorage(uid, tenantId) || {};
    localStorage.setItem(key, JSON.stringify({
      ...existing,
      ...layout,
      tenantId,
      schemaVersion: SCHEMA_VERSION,
    }));
    return true;
  } catch {
    return false;
  }
};

/** True when this cache was written by an explicit Launcher Settings save. */
export const hasSavedLauncherSettings = (cached) => Boolean(cached?.launcherSettingsSaved);

const iconId = (icon) => (icon && icon.id != null ? String(icon.id) : '');

/**
 * Assigned Home Designer layouts stay live after publish.
 * Users keep extra tiles they added; dealer/admin published tiles always win.
 */
export const resolveUserWorkspaceLayout = (
  published = {},
  cached,
  workspaceId,
  uid = null,
  publishedVersionId = null,
) => {
  const base = overlayLauncherChrome(published, cached, uid);
  const publishedIcons = Array.isArray(published.icons) ? published.icons : [];
  const publishedIds = new Set(publishedIcons.map(iconId).filter(Boolean));
  const sameWorkspace = Boolean(
    workspaceId && cached && cached.seededFromWorkspaceId === workspaceId
  );
  const extras = sameWorkspace && Array.isArray(cached.icons)
    ? cached.icons.filter((icon) => {
        const id = iconId(icon);
        return id && !publishedIds.has(id);
      })
    : [];
  return {
    ...base,
    icons: extras.length ? [...publishedIcons, ...extras] : publishedIcons,
    seededFromWorkspaceId: workspaceId || cached?.seededFromWorkspaceId || null,
    seededFromVersionId: publishedVersionId || cached?.seededFromVersionId || null,
    usedPersonalTiles: extras.length > 0,
  };
};

/** Overlay per-user chrome (theme, grid, background) onto a base layout. */
export const overlayLauncherChrome = (base = {}, cached, uid = null) => {
  if (!cached || typeof cached !== 'object') return base;

  const next = hasSavedLauncherSettings(cached)
    ? {
        ...base,
        theme: cached.theme ?? base.theme,
        gridColumns: cached.gridColumns ?? base.gridColumns,
        gridRows: cached.gridRows ?? base.gridRows,
        iconSize: cached.iconSize ?? base.iconSize,
        gap: cached.gap ?? base.gap,
        showGrid: cached.showGrid ?? base.showGrid,
        preset: cached.preset ?? base.preset,
      }
    : { ...base };

  // Background: dedicated store wins, then cached layout field.
  const dedicatedBg = uid ? readLauncherBackgroundUrlSync(uid) : '';
  if (dedicatedBg) {
    next.backgroundImageUrl = dedicatedBg;
  } else if (Object.prototype.hasOwnProperty.call(cached, 'backgroundImageUrl')) {
    next.backgroundImageUrl = cached.backgroundImageUrl || '';
  }

  return next;
};

export const loadLayoutFromApi = async (tenantId) => {
  const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
  const res = await fetchWithAuth(`/api/v2/users/me/prefs/layout${qs}`, 'GET', null, {
    suppressErrorLog: true,
  });
  return res?.data || {};
};
